/**
 * THE TWO QUESTIONS `schedule.pattern` HAS BEEN ASKING AT ONCE.
 *
 * A schedule answer is two independent facts:
 *
 *   the DAY PATTERN     which days the role commits to -- Weekdays, Weekends,
 *                       Flexible, On-Call Shifts. The axis a PROVIDER states a
 *                       preference on.
 *   the SHIFT STRUCTURE the shape of a week -- three twelves, four tens. The
 *                       axis a FEED almost always publishes.
 *
 * Measured on staging 2026-08-26 across 2,978 listings carrying a pattern:
 * 2,921 (98.1%) hold an NxM shift code, 24 (0.8%) hold a day word. So the
 * editor's four-chip PATTERN control could not represent what nearly every
 * listing actually says, and the matcher was comparing the two axes against
 * each other and scoring zero.
 *
 * Neither half is EVER inferred from the other: a 5x8 is five eight-hour shifts
 * and says nothing about which five days.
 *
 * EXACT TWIN of backend/src/common/utils/schedule-vocabulary.util.ts. The pair
 * only works while they agree, and both specs carry the same cases so a change
 * to one fails the other.
 */

/** The day-commitment axis. Closed, and shared with the provider preference. */
export const DAY_PATTERNS = ['Flexible', 'Weekdays', 'Weekends', 'On-Call Shifts'];

/**
 * Shift lengths an anesthesia department actually rosters, in hours. 13 is a
 * twelve plus handover (19 real rows on staging); 24 is an ordinary call shift.
 * Anything outside this set is read as a misparse and refused.
 */
const SHIFT_HOURS = new Set([4, 6, 8, 10, 12, 13, 16, 24]);
const MAX_DAYS_PER_WEEK = 7;
const BLOCK_SEVEN_ON_SEVEN_OFF = '7on7off';

const WORD_NUMBERS = {
  one: 1, two: 2, three: 3, four: 4, five: 5, six: 6,
  seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12,
};

const norm = (value) =>
  String(value ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');

const DAY_SYNONYMS = {
  flex: 'Flexible',
  flexibleschedule: 'Flexible',
  varies: 'Flexible',
  varied: 'Flexible',
  variesbysite: 'Flexible',
  variesbylocation: 'Flexible',
  asneeded: 'Flexible',
  prnflexible: 'Flexible',
  anydays: 'Flexible',
  weekday: 'Weekdays',
  weekdaysonly: 'Weekdays',
  monfri: 'Weekdays',
  mondayfriday: 'Weekdays',
  mondaytofriday: 'Weekdays',
  mtof: 'Weekdays',
  threeweekdays: 'Weekdays',
  fourweekdays: 'Weekdays',
  twoweekdays: 'Weekdays',
  weekend: 'Weekends',
  weekendsonly: 'Weekends',
  weekendcoverage: 'Weekends',
  prnweekends: 'Weekends',
  oncall: 'On-Call Shifts',
  oncallonly: 'On-Call Shifts',
  // The provider side stores this bare word and DAY_PATTERNS does not contain
  // it, so without this a provider who asked for call could never match a
  // listing that offers it.
  call: 'On-Call Shifts',
  callcoverage: 'On-Call Shifts',
  callonly: 'On-Call Shifts',
  prncall: 'On-Call Shifts',
};

/**
 * The day commitment a value states, or null when it states none.
 * Null means the days are UNKNOWN -- no signal, never a mismatch.
 */
export function dayPatternOf(value) {
  const n = norm(value);
  if (!n) return null;
  const exact = DAY_PATTERNS.find((p) => norm(p) === n);
  if (exact) return exact;
  if (DAY_SYNONYMS[n]) return DAY_SYNONYMS[n];
  if (/weekend/.test(n)) return 'Weekends';
  if (/weekday|monfri/.test(n)) return 'Weekdays';
  if (/oncall/.test(n)) return 'On-Call Shifts';
  if (/flexib|varies/.test(n)) return 'Flexible';
  return null;
}

function structureFrom(days, hours) {
  if (days >= 1 && days <= MAX_DAYS_PER_WEEK && SHIFT_HOURS.has(hours)) {
    return `${days}x${hours}`;
  }
  // Hours-first ("8x5" is 24 rows on staging), righted ONLY where the first
  // number cannot be a day count and the second can -- the one unambiguous
  // reading. "8x24" is refused rather than guessed at.
  if (
    days > MAX_DAYS_PER_WEEK &&
    hours >= 1 &&
    hours <= MAX_DAYS_PER_WEEK &&
    SHIFT_HOURS.has(days)
  ) {
    return `${hours}x${days}`;
  }
  return null;
}

/** The shift shape a value states, canonicalised to `NxM`, or null. */
export function shiftStructureOf(value) {
  const raw = String(value ?? '');
  const n = norm(raw);
  if (!n) return null;

  if (/^(\d+)on\1off$/.test(n) || /7on7off/.test(n)) {
    return BLOCK_SEVEN_ON_SEVEN_OFF;
  }

  const digits = raw.match(/(\d{1,2})\s*[x×]\s*(\d{1,2})/i);
  if (digits) return structureFrom(Number(digits[1]), Number(digits[2]));

  const words = n.match(
    /^(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|\d{1,2})(\d{1,2})(s|hour|hours|hourshift|hourshifts|shift|shifts)?$/,
  );
  if (words) {
    const days = WORD_NUMBERS[words[1]] ?? Number(words[1]);
    return structureFrom(days, Number(words[2]));
  }
  return null;
}

/** Both halves of what a stored pattern says. Either may be null. */
export function parseSchedulePattern(value) {
  return {
    dayPattern: dayPatternOf(value),
    shiftStructure: shiftStructureOf(value),
  };
}

/**
 * The shift shapes the card editor offers as chips, commonest first by real
 * volume on staging (4x10 x1118, 5x8 x1114, 3x12 x268, 5x10 x137, 4x8 x66,
 * 2x12 x43, 2x24 x25).
 *
 * A CONVENIENCE, not the closed set of what may be stored: shiftStructureOf
 * accepts any believable NxM, so a roster this list does not name still
 * round-trips through the card rather than being erased by it.
 */
export const SHIFT_STRUCTURES = [
  { value: '4x10', label: 'Four 10s' },
  { value: '5x8', label: 'Five 8s' },
  { value: '3x12', label: 'Three 12s' },
  { value: '5x10', label: 'Five 10s' },
  { value: '4x8', label: 'Four 8s' },
  { value: '2x12', label: 'Two 12s' },
  { value: '2x24', label: 'Two 24s' },
  { value: '7on7off', label: '7 on / 7 off' },
];
